// /wms/* → the WMS drill-down: Warehouse → Merchant → Modules.
//
//   /wms                                  → pick a warehouse
//   /wms/w/:warehouseId                   → pick a merchant in that warehouse
//   /wms/w/:warehouseId/m/:merchantId/... → the module screens, scoped to both
//
// The old flat URLs (/wms/inventory, /wms/inbound/grn, …) still resolve: they land on the warehouse
// picker rather than 404ing, because bookmarks and pasted links to them are everywhere.
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { wmsScope } from '@/api/client';
import { WmsPage, WMS_SECTIONS } from './WmsPage';
import { WmsScopeContext, WarehousePicker, MerchantPicker, WmsScopeBar, WmsModuleNav, WmsAdminBar } from './WmsScope';

export function WmsRoutes() {
  const { pathname } = useLocation();
  const tail = pathname.replace(/^\/wms\/?/, '').replace(/\/+$/, '');

  const m = tail.match(/^w\/(\d+)(?:\/m\/(\d+))?/i);
  const scopeWarehouse = m ? Number(m[1]) : null;
  const scopeMerchant = m?.[2] ? Number(m[2]) : null;

  // Publish the scope for the API layer, and CLEAR it on the way out — otherwise leaving WMS would
  // leave every later WMS request silently filtered to a merchant nobody selected.
  useEffect(() => {
    wmsScope.warehouseId = scopeWarehouse;
    wmsScope.merchantId = scopeMerchant;
    return () => { wmsScope.warehouseId = null; wmsScope.merchantId = null; };
  }, [scopeWarehouse, scopeMerchant]);

  // /wms → warehouse picker
  if (!tail) return <WarehousePicker />;

  const scoped = tail.match(/^w\/(\d+)(?:\/m\/(\d+)(?:\/(.*))?)?$/i);
  if (scoped) {
    const warehouseId = Number(scoped[1]);
    const merchantId = scoped[2] ? Number(scoped[2]) : null;

    // /wms/w/:id → merchant picker
    if (!merchantId) return <MerchantPicker warehouseId={warehouseId} />;

    const section = (scoped[3] ?? 'dashboard').toLowerCase();
    if (!WMS_SECTIONS[section]) return <Navigate to={`/wms/w/${warehouseId}/m/${merchantId}/dashboard`} replace />;

    return (
      <WmsScopeContext.Provider value={{ warehouseId, merchantId }}>
        <div className="space-y-3">
          <WmsScopeBar warehouseId={warehouseId} merchantId={merchantId} />
          <WmsModuleNav warehouseId={warehouseId} merchantId={merchantId} active={section} />
          <WmsPage section={section} />
        </div>
      </WmsScopeContext.Provider>
    );
  }

  // Warehouse-level administration — the warehouse master, the merchant allocations that decide who
  // is IN a warehouse, and suppliers. These are settings for the building, not one merchant's goods,
  // so they stay unscoped and are reached from the warehouse picker rather than from inside a
  // merchant. Without this they would be unreachable, and allocations are what populate step 2.
  const ADMIN_SECTIONS = ['warehouses', 'masters/merchants', 'masters/suppliers'];
  const admin = tail.toLowerCase();
  if (ADMIN_SECTIONS.includes(admin) && WMS_SECTIONS[admin]) {
    return (
      <div className="space-y-3">
        <WmsAdminBar active={admin} />
        <WmsPage section={admin} />
      </div>
    );
  }

  // Any other legacy flat section URL — send them to the picker rather than showing every merchant's
  // goods mixed together, which is the thing this structure exists to stop.
  return <Navigate to="/wms" replace />;
}
